import { Link } from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, CheckCircle2, Mail, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { useEffect, useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import { PlanPicker } from "@/components/PlanPicker";
import { grantEmailBonus } from "@/lib/usage";

type Props = {
  source: "mini-quiz" | "practice-limit" | "results";
  className?: string;
  /** "soft" = diskret reservväg under en dominerande köpknapp. */
  variant?: "default" | "soft";
  /** Körs när e-posten sparats och bonusfrågorna låsts upp. */
  onCaptured?: () => void;
};

export function SignupNudge({ source, className, variant = "default", onCaptured }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    track("Signup Nudge Shown", { source });
  }, [source]);

  const bullets = t("aSignupNudge.bullets", { returnObjects: true }) as string[];

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/leads/capture-practice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      if (res.ok) {
        track("Practice Lead Captured", { source });
        // Belöning direkt: 5 bonusfrågor låses upp samma dag.
        grantEmailBonus();
        setSubmitted(true);
        onCaptured?.();
      }
    } catch {
      // Tysta fel.
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === "soft" && !user) {
    return (
      <div className={`rounded-xl border border-border bg-muted/40 p-4 ${className ?? ""}`}>
        <p className="text-sm font-semibold text-foreground">{t("aSignupNudge.softTitle")}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t("aSignupNudge.softBody")}</p>
        {!submitted ? (
          <form onSubmit={handleLeadSubmit} className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("aSignupNudge.leadPlaceholder")}
              aria-label={t("aSignupNudge.leadPlaceholder")}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-secondary disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? t("aSignupNudge.leadSending") : t("aSignupNudge.softCta")}
            </button>
          </form>
        ) : (
          <p className="mt-3 flex items-center gap-2 text-sm font-medium text-foreground">
            <CheckCircle2 className="h-4 w-4 text-success" /> {t("aSignupNudge.leadSuccess")}
          </p>
        )}
        {submitted && !onCaptured && (
        <Link
          to="/practice"
          hash="start"
          onClick={() => track("Bonus Questions Start", { source })}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          {t("aSignupNudge.bonusCta")}
        </Link>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">{t("aSignupNudge.softFootnote")}</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 p-5 sm:p-6 shadow-card ${className ?? ""}`}
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
        <Sparkles className="h-3.5 w-3.5" /> {t("aSignupNudge.badge")}
      </div>
      <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold">
        {t("aSignupNudge.title")}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("aSignupNudge.body")}
      </p>
      <ul className="mt-4 grid gap-2 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" /> {b}
          </li>
        ))}
      </ul>

      {!user && !submitted && (
        <form onSubmit={handleLeadSubmit} className="mt-5 space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("aSignupNudge.leadPlaceholder")}
              className="w-full rounded-xl border border-border bg-background pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? t("aSignupNudge.leadSending") : t("aSignupNudge.leadCta")}
          </button>
          <p className="text-center text-[11px] text-muted-foreground">
            {t("aSignupNudge.leadFootnote")}
          </p>
        </form>
      )}

      {!user && submitted && (
        <div className="mt-5 rounded-xl border border-success/30 bg-success/10 px-4 py-4 text-sm text-foreground">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4 text-success" />
            {t("aSignupNudge.leadSuccess")}
          </div>
          <Link
            to="/practice"
            hash="start"
            onClick={() => track("Bonus Questions Start", { source })}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            {t("aSignupNudge.bonusCta")}
          </Link>
        </div>
      )}

      {!user && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/signup"
            onClick={() => track("Signup Nudge Click", { source, action: "signup" })}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-medium hover:bg-secondary"
          >
            {t("aSignupNudge.cta")} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/login"
            onClick={() => track("Signup Nudge Click", { source, action: "login" })}
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-medium hover:bg-secondary"
          >
            {t("aSignupNudge.login")}
          </Link>
        </div>
      )}

      {user && (
        <div className="mt-5" data-android-hide>
          <PlanPicker source={`signup-nudge-${source}`} />
          <Link to="/pricing" className="mt-3 inline-block text-sm text-accent underline underline-offset-2 hover:no-underline">
            {t("aSignupNudge.comparePlans")}
          </Link>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">{t("aSignupNudge.footnote")}</p>
    </div>
  );
}
